import { useState, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchTickets, bulkTicketAction } from '@/lib/api';
import { useNexusStore } from '@/stores/nexusStore';
import { StatusDot } from '@/components/shared/StatusDot';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertTriangle, Filter, Search, ChevronRight, RefreshCw, CheckSquare, Square, X, UserCog, ArrowUpCircle, XCircle } from 'lucide-react';
import socket from '@/lib/socket';
import { toast } from 'sonner';

const PRIORITY_STYLES = {
  P1: 'badge-p1', P2: 'badge-p2', P3: 'badge-p3', P4: 'badge-p4', P5: 'badge-p5'
};

const STATUS_MAP = {
  NEW: { label: 'New', dot: 'info' },
  TRIAGING: { label: 'Triaging', dot: 'processing' },
  IN_PROGRESS: { label: 'In Progress', dot: 'active' },
  AGENT_RESOLVING: { label: 'Agent Resolving', dot: 'processing' },
  PENDING_APPROVAL: { label: 'Pending Approval', dot: 'warning' },
  RESOLVED: { label: 'Resolved', dot: 'success' },
  CLOSED: { label: 'Closed', dot: 'idle' },
};

// Live SLA countdown component
const SlaCountdown = ({ deadline }) => {
  const [remaining, setRemaining] = useState('');
  const [urgency, setUrgency] = useState('green');

  useEffect(() => {
    if (!deadline) return;
    const update = () => {
      const now = new Date();
      const dl = new Date(deadline);
      const diffMs = dl - now;
      if (diffMs <= 0) { setRemaining('BREACHED'); setUrgency('red'); return; }
      const hours = Math.floor(diffMs / 3600000);
      const minutes = Math.floor((diffMs % 3600000) / 60000);
      const secs = Math.floor((diffMs % 60000) / 1000);
      if (hours > 0) setRemaining(`${hours}h ${minutes}m`);
      else if (minutes > 0) setRemaining(`${minutes}m ${secs}s`);
      else setRemaining(`${secs}s`);
      if (diffMs < 3600000) setUrgency('red');
      else if (diffMs < 14400000) setUrgency('orange');
      else setUrgency('green');
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [deadline]);

  const colorClass = urgency === 'red' ? 'text-[var(--nexus-danger)]' : urgency === 'orange' ? 'text-[var(--nexus-warning)]' : 'text-[var(--nexus-success)]';

  return <span className={`font-mono text-[10px] tabular-nums ${colorClass}`}>{remaining}</span>;
};

export default function IncidentsPage() {
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('INCIDENT');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const queryClient = useQueryClient();
  const agentStatuses = useNexusStore((s) => s.agentStatuses);

  const { data: tickets, isLoading, refetch } = useQuery({
    queryKey: ['tickets', filterType, filterPriority, filterStatus],
    queryFn: () => fetchTickets({
      ticket_type: filterType || undefined,
      priority: filterPriority || undefined,
      status: filterStatus || undefined,
    }),
    refetchInterval: 10000,
  });

  // Listen for live ticket updates via WebSocket
  useEffect(() => {
    const handleTicketUpdate = (data) => {
      queryClient.invalidateQueries(['tickets']);
    };
    const handleTicketCreated = (data) => {
      queryClient.invalidateQueries(['tickets']);
      queryClient.invalidateQueries(['nav-badges']);
    };
    socket.on('ticket:updated', handleTicketUpdate);
    socket.on('ticket:created', handleTicketCreated);
    return () => {
      socket.off('ticket:updated', handleTicketUpdate);
      socket.off('ticket:created', handleTicketCreated);
    };
  }, [queryClient]);

  const filtered = (tickets || []).filter(t =>
    !searchTerm || t.title?.toLowerCase().includes(searchTerm.toLowerCase()) || t.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Bulk action mutation
  const bulkMutation = useMutation({
    mutationFn: async ({ action, value }) => bulkTicketAction([...selectedIds], action, value),
    onSuccess: (data) => {
      toast.success(`Bulk ${data.action || 'action'}: ${data.updated} tickets updated`);
      setSelectedIds(new Set());
      queryClient.invalidateQueries(['tickets']);
    },
    onError: () => toast.error('Bulk action failed'),
  });

  const toggleSelect = (id) => {
    setSelectedIds(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };
  const toggleAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(t => t.id)));
  };

  // Check which tickets have ARIA actively working
  const ariaWorking = agentStatuses['ARIA']?.status === 'PROCESSING';
  const ariaTicket = agentStatuses['ARIA']?.current_task;

  return (
    <div data-testid="incidents-page" className="w-full max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Incidents</h1>
          <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">{filtered.length} tickets found</p>
        </div>
        <button onClick={() => refetch()} data-testid="incidents-refresh" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] text-xs text-[var(--nexus-text-secondary)] hover:bg-white/[0.04] transition-colors">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Priority toggle bar */}
      <div className="flex items-center gap-1 mb-3">
        {['', 'P1', 'P2', 'P3', 'P4'].map(p => (
          <button key={p} onClick={() => setFilterPriority(p)}
            data-testid={`priority-filter-${p || 'all'}`}
            className={`px-3 py-1 rounded-md text-[10px] font-mono font-medium transition-colors ${
              filterPriority === p
                ? (p === 'P1' ? 'bg-red-500/20 text-red-300 border border-red-500/30' :
                   p === 'P2' ? 'bg-amber-500/20 text-amber-200 border border-amber-500/30' :
                   p === 'P3' ? 'bg-indigo-500/20 text-indigo-200 border border-indigo-500/30' :
                   p === 'P4' ? 'bg-slate-500/20 text-slate-200 border border-slate-500/30' :
                   'bg-[var(--nexus-accent)]/15 text-[var(--nexus-accent)] border border-cyan-500/30')
                : 'bg-[var(--nexus-bg-elevated)] text-[var(--nexus-text-muted)] border border-[var(--nexus-border)] hover:bg-white/[0.04]'
            }`}>
            {p || 'ALL'}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)]">
          <Search size={12} className="text-[var(--nexus-text-muted)]" />
          <input data-testid="incidents-search" type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search tickets..." className="bg-transparent text-xs text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none w-48" />
        </div>
        <select data-testid="filter-type" value={filterType} onChange={e => setFilterType(e.target.value)}
          className="px-2.5 py-1.5 rounded-lg bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] text-xs text-[var(--nexus-text-secondary)] outline-none">
          <option value="">All Types</option>
          <option value="INCIDENT">Incidents</option>
          <option value="CHANGE">Changes</option>
          <option value="REQUEST">Requests</option>
          <option value="PROBLEM">Problems</option>
          <option value="SECURITY">Security</option>
        </select>
        <select data-testid="filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-2.5 py-1.5 rounded-lg bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] text-xs text-[var(--nexus-text-secondary)] outline-none">
          <option value="">All Statuses</option>
          <option value="NEW">New</option>
          <option value="TRIAGING">Triaging</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="AGENT_RESOLVING">Agent Resolving</option>
          <option value="PENDING_APPROVAL">Pending Approval</option>
          <option value="RESOLVED">Resolved</option>
        </select>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 mb-3 p-2.5 rounded-xl bg-[var(--nexus-accent)]/10 border border-cyan-500/20 animate-nexus-slide-in" data-testid="bulk-action-bar">
          <span className="text-xs text-[var(--nexus-accent)] font-mono">{selectedIds.size} selected</span>
          <div className="w-px h-4 bg-[var(--nexus-border)]" />
          <button onClick={() => bulkMutation.mutate({ action: 'reassign', value: 'ARIA' })} className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] text-[var(--nexus-text-secondary)] hover:bg-white/[0.04]" data-testid="bulk-reassign"><UserCog size={10} /> Reassign</button>
          <button onClick={() => bulkMutation.mutate({ action: 'escalate' })} className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] bg-amber-500/10 border border-amber-500/20 text-amber-200 hover:bg-amber-500/20" data-testid="bulk-escalate"><ArrowUpCircle size={10} /> Escalate</button>
          <button onClick={() => bulkMutation.mutate({ action: 'close' })} className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] bg-red-500/10 border border-red-500/20 text-red-300 hover:bg-red-500/20" data-testid="bulk-close"><XCircle size={10} /> Close</button>
          <button onClick={() => setSelectedIds(new Set())} className="ml-auto p-1 text-[var(--nexus-text-muted)] hover:text-[var(--nexus-text-secondary)]"><X size={12} /></button>
        </div>
      )}

      {/* Table */}
      <div className="nexus-card relative overflow-hidden">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {[...Array(8)].map((_, i) => <div key={i} className="h-10 rounded-lg bg-[var(--nexus-bg-base)] animate-pulse" />)}
          </div>
        ) : (
          <ScrollArea className="h-[calc(100vh-280px)]">
            <table className="w-full" data-testid="incidents-table">
              <thead className="sticky top-0 bg-[var(--nexus-bg-elevated)] z-10">
                <tr className="border-b border-[var(--nexus-border)]">
                  <th className="px-2 py-2.5 w-8">
                    <button onClick={toggleAll} className="text-[var(--nexus-text-muted)] hover:text-[var(--nexus-text-secondary)]" data-testid="select-all">
                      {selectedIds.size === filtered.length && filtered.length > 0 ? <CheckSquare size={14} /> : <Square size={14} />}
                    </button>
                  </th>
                  <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-left px-2 py-2.5 font-medium w-8"></th>
                  <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-left px-3 py-2.5 font-medium">ID</th>
                  <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-left px-3 py-2.5 font-medium">Priority</th>
                  <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-left px-3 py-2.5 font-medium">Status</th>
                  <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-left px-3 py-2.5 font-medium">Title</th>
                  <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-left px-3 py-2.5 font-medium">Agent</th>
                  <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-left px-3 py-2.5 font-medium">SLA</th>
                  <th className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] text-left px-3 py-2.5 font-medium">Confidence</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((ticket) => {
                  const sm = STATUS_MAP[ticket.status] || { label: ticket.status, dot: 'info' };
                  const isAriaOnThis = ticket.status === 'AGENT_RESOLVING' && ticket.assigned_agent === 'ARIA';
                  return (
                    <tr key={ticket.id} className={`border-b border-[var(--nexus-border)] hover:bg-white/[0.02] transition-colors group ${selectedIds.has(ticket.id) ? 'bg-[var(--nexus-accent)]/5' : ''}`}>
                      {/* Checkbox */}
                      <td className="px-2 py-2">
                        <button onClick={() => toggleSelect(ticket.id)} className="text-[var(--nexus-text-muted)] hover:text-[var(--nexus-text-secondary)]">
                          {selectedIds.has(ticket.id) ? <CheckSquare size={14} className="text-[var(--nexus-accent)]" /> : <Square size={14} />}
                        </button>
                      </td>
                      {/* Agent working indicator */}
                      <td className="px-2 py-2">
                        {isAriaOnThis && (
                          <div className="w-2 h-2 rounded-full bg-[var(--nexus-accent)] animate-nexus-pulse-dot" title="ARIA is working this ticket" />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Link to={`/incidents/${ticket.id}`} className="font-mono text-xs text-[var(--nexus-primary-bright)] hover:underline" data-testid={`ticket-link-${ticket.id}`}>
                          {ticket.id}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${PRIORITY_STYLES[ticket.priority] || 'badge-p4'}`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <StatusDot status={sm.dot} pulse={sm.dot === 'processing'} size="sm" />
                          <span className="text-xs text-[var(--nexus-text-secondary)]">{sm.label}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Link to={`/incidents/${ticket.id}`} className="text-xs text-[var(--nexus-text-primary)] hover:text-[var(--nexus-accent)] transition-colors">
                          {ticket.title}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        <span className="font-mono text-[10px] text-[var(--nexus-accent)]">{ticket.assigned_agent}</span>
                      </td>
                      {/* Live SLA countdown */}
                      <td className="px-3 py-2">
                        {ticket.status !== 'RESOLVED' && ticket.status !== 'CLOSED' && ticket.sla_deadline ? (
                          <SlaCountdown deadline={ticket.sla_deadline} />
                        ) : (
                          <span className="font-mono text-[10px] text-[var(--nexus-text-muted)]">
                            {ticket.status === 'RESOLVED' ? 'Resolved' : '-'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {ticket.confidence > 0 && (
                          <div className="flex items-center gap-1.5">
                            <div className="w-10 h-1.5 rounded-full bg-[var(--nexus-bg-base)] overflow-hidden">
                              <div className="h-full rounded-full bg-[var(--nexus-accent)] transition-all" style={{ width: `${ticket.confidence}%` }} />
                            </div>
                            <span className="font-mono text-[10px] text-[var(--nexus-text-muted)] tabular-nums">{ticket.confidence}%</span>
                          </div>
                        )}
                      </td>
                      <td className="px-1">
                        <Link to={`/incidents/${ticket.id}`} className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <ChevronRight size={14} className="text-[var(--nexus-text-muted)]" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-12 text-[var(--nexus-text-muted)] text-sm">No tickets match your filters</td></tr>
                )}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}
